/**
 * Header, footer and hero content that used to come from Storyblok.
 *
 * Storyblok was fetching three stories — `panorama`, `focus` and `home` — whose
 * entire payload was the values below. Worse, the layout component holding
 * `<router-outlet>` was itself rendered from that response, so no route painted
 * until the CMS round-trip finished. The content is static marketing copy and
 * navigation, so it lives here and the app renders immediately.
 *
 * Trade-off: editing any of this now needs a commit and a deploy, where before a
 * non-developer could change it in the Storyblok panel.
 *
 * Offering and knowledge pages are unaffected — their blocks are built from
 * Mulesoft data by `OfferingsService.buildOfferingComponents()` and still render
 * through `ndc-dynamic` against the `Components` registry.
 */

export interface NavigationLink {
  link: string;
  title: string;
  isExternal?: boolean;
}

export interface SecondaryNavigationSection {
  link: string;
  title: string;
  menuName: string;
}

export interface HeaderConfig {
  appTitle: string;
  navigation: NavigationLink[];
  secondaryNavigation: SecondaryNavigationSection[];
  showSecondaryNavigation: boolean;
  showSearch: boolean;
  showFeedbackButton: boolean;
  feedbackButtonTitle: string;
}

export interface FooterConfig {
  navigation: NavigationLink[];
  disclaimer: string;
  sitemap: unknown[];
}

/** Shared by both modes; only the header's secondary navigation differs. */
const PRIMARY_NAVIGATION: NavigationLink[] = [
  { link: '/offerings/panorama', title: 'About us' },
  { link: '/focus/my-offerings', title: 'My offerings', isExternal: false },
];

export const PANORAMA_HEADER: HeaderConfig = {
  appTitle: 'Panorama',
  navigation: PRIMARY_NAVIGATION,
  secondaryNavigation: [
    { link: '/all-offerings', title: 'Offerings', menuName: 'offerings' },
  ],
  showSecondaryNavigation: true,
  showSearch: false,
  showFeedbackButton: true,
  feedbackButtonTitle: 'Give us feedback',
};

export const FOCUS_HEADER: HeaderConfig = {
  ...PANORAMA_HEADER,
  secondaryNavigation: [],
  showSecondaryNavigation: false,
};

/** Identical in both stories, so there is only one. */
export const SHARED_FOOTER: FooterConfig = {
  sitemap: [],
  disclaimer:
    'This website contains information on products which is targeted to a wide ' +
    'range of audiences and could contain product details or information ' +
    'otherwise not accessible or valid in your country. Please be aware that we ' +
    'do not take any responsibility for accessing such information which may not ' +
    'comply with any legal process, regulation, registration or usage in the ' +
    'country of your origin',
  navigation: [
    { link: '/', title: 'Home page' },
    { link: '/search', title: 'All offerings' },
    { link: '/offerings/panorama', title: 'About us' },
    { link: '/focus', title: 'My offerings' },
    {
      link: 'https://globaljira.roche.com/ideahub#/portal/64',
      title: 'Submit an Idea',
      isExternal: true,
    },
  ],
};

/**
 * Homepage hero copy.
 *
 * Reworded from marketing statement to question per the `-assistant agent`
 * Figma canvas (frame `start`): the hero is now the single entry point into the
 * catalog, so it asks what you need rather than describing the platform.
 */
export const HOME_HERO = {
  title: 'What can I help you find?',
  description:
    "Search Roche's IT catalog, compare offerings, check eligibility, and " +
    'request access.',
  searchPlaceholder: 'Ask anything about Panorama offerings...',
  searchButtonText: 'Ask Panorama',
} as const;
